import React, { useEffect, useRef, useState } from 'react';
import { Mic, StopCircle, X as XIcon, Send, Loader2 } from 'lucide-react';

const MAX_SECONDS = 60;

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

/**
 * Compact tap-to-record voice recorder.
 * - Tap mic to start recording; tap again (or Stop button) to stop.
 * - Renders live timer and cancel/send controls.
 * - Calls `onSend(blobFile, durationSeconds)` when user taps Send.
 */
const VoiceRecorder = ({ onSend, onError, sending }) => {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);

  const cleanupMedia = () => {
    try { mediaRecorderRef.current?.stream?.getTracks?.().forEach(t => t.stop()); } catch { /* ignore */ }
    mediaRecorderRef.current = null;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => () => {
    cleanupMedia();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      onError?.('Mic not supported on this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Choose best supported mimeType
      const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
      const mime = preferred.find(m => window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) || '';
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type });
        const file = new File([b], `voice-${Date.now()}.${type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'}`, { type });
        setBlob(file);
        const url = URL.createObjectURL(b);
        setPreviewUrl(url);
        cleanupMedia();
      };
      mr.start();
      startedAtRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        const sec = (Date.now() - startedAtRef.current) / 1000;
        setElapsed(sec);
        if (sec >= MAX_SECONDS) stop();
      }, 100);
    } catch (e) {
      onError?.('Microphone permission denied');
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && recording) {
      try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
      setRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  const cancel = () => {
    if (recording) {
      try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
    }
    cleanupMedia();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setRecording(false);
    setBlob(null);
    setPreviewUrl(null);
    setElapsed(0);
  };

  const handleSend = () => {
    if (!blob) return;
    onSend?.(blob, elapsed);
  };

  // Recording state
  if (recording) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-3 py-2 flex-1" data-testid="voice-recording">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-semibold text-red-700 min-w-[42px]">{fmtTime(elapsed)}</span>
        <span className="text-xs text-red-500 flex-1">Recording… max {MAX_SECONDS}s</span>
        <button onClick={cancel} className="p-1.5 rounded-full hover:bg-red-100" aria-label="Cancel" data-testid="voice-cancel-btn">
          <XIcon className="w-4 h-4 text-red-500" />
        </button>
        <button onClick={stop} className="p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600" aria-label="Stop" data-testid="voice-stop-btn">
          <StopCircle className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Preview state (has blob, awaiting send)
  if (blob && previewUrl) {
    return (
      <div className="flex items-center gap-2 bg-[#009E60]/5 border border-[#009E60]/20 rounded-full px-2 py-1.5 flex-1" data-testid="voice-preview">
        <audio src={previewUrl} controls className="flex-1 h-8" data-testid="voice-preview-audio" />
        <span className="text-xs font-semibold text-[#009E60]">{fmtTime(elapsed)}</span>
        <button onClick={cancel} disabled={sending} className="p-1.5 rounded-full hover:bg-red-100 disabled:opacity-50" aria-label="Discard" data-testid="voice-discard-btn">
          <XIcon className="w-4 h-4 text-red-500" />
        </button>
        <button onClick={handleSend} disabled={sending} className="p-1.5 rounded-full bg-[#009E60] text-white hover:bg-[#007A4B] disabled:opacity-50" aria-label="Send voice" data-testid="voice-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  // Idle: mic button
  return (
    <button
      type="button"
      onClick={start}
      className="h-12 w-12 rounded-full flex items-center justify-center hover:bg-gray-100 flex-shrink-0"
      aria-label="Record voice note"
      data-testid="voice-record-btn"
    >
      <Mic className="w-6 h-6 text-[#009E60]" />
    </button>
  );
};

export default VoiceRecorder;
